import React, { useState } from 'react';
import styled from 'styled-components';
import { Draggable } from 'react-beautiful-dnd';
import { useTemplate } from '../../context/TemplateContext';

const EmailElement = ({ element, index, onClick, isSelected }) => {
  const { removeElement, duplicateElement, setElementLayout, moveElementUp, moveElementDown } = useTemplate();
  const [showControls, setShowControls] = useState(false);

  const renderElementContent = () => {
    switch (element.type) {
      case 'header':
        return (
          <HeaderElement style={element.styles}>
            {element.content}
          </HeaderElement>
        );
      case 'text':
        return (
          <TextElement style={element.styles}>
            {element.content}
          </TextElement>
        );
      case 'image':
        return (
          <ImageContainer style={element.styles}>
            <img 
              src={element.src || element.defaultSrc} 
              alt={element.alt || element.defaultAlt} 
              style={{ maxWidth: '100%', width: element.width || '100%' }} 
            />
          </ImageContainer>
        );
      case 'button':
        return (
          <ButtonContainer style={element.styles}>
            <ButtonElement 
              style={{ 
                backgroundColor: element.styles?.buttonColor || '#5876ff',
                color: element.styles?.textColor || '#ffffff',
                borderRadius: element.styles?.borderRadius || '4px'
              }}
            >
              {element.content}
            </ButtonElement>
          </ButtonContainer>
        );
      case 'spacer':
        return <SpacerElement style={{ height: element.height || '20px' }} />;
      case 'divider':
        return (
          <DividerContainer style={element.styles}>
            <hr style={{ borderTop: `1px solid ${element.styles?.color || '#dddddd'}`, margin: 0 }} />
          </DividerContainer>
        );
      case 'footer':
        return (
          <FooterElement style={element.styles}>
            {element.content}
            {element.includeUnsubscribe && (
              <UnsubscribeLink>
                <a href="#" style={{ color: element.styles?.color || '#666666' }}>Unsubscribe</a>
              </UnsubscribeLink>
            )}
          </FooterElement>
        );
      default:
        return <div>Unknown element type</div>;
    }
  };

  return (
    <Draggable draggableId={element.id} index={index}>
      {(provided, snapshot) => (
        <ElementContainer
          ref={provided.innerRef}
          {...provided.draggableProps}
          isDragging={snapshot.isDragging}
          isSelected={isSelected}
          onClick={onClick}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
          layout={element.layout || 'full'} // Can be 'full', 'left', 'right'
        >

          
          {/* Hover controls */}
          <HoverControls className={showControls || isSelected ? 'visible' : ''}>
            <ControlButton 
              title="Move to left column" 
              onClick={(e) => {
                e.stopPropagation();
                setElementLayout(element.id, 'left');
              }}
            >
              <i className="fas fa-arrow-left"></i>
            </ControlButton>
            
            <ControlButton 
              title="Full width" 
              onClick={(e) => {
                e.stopPropagation();
                setElementLayout(element.id, 'full');
              }}
            >
              <i className="fas fa-arrows-alt-h"></i>
            </ControlButton>
            
            <ControlButton 
              title="Move to right column" 
              onClick={(e) => {
                e.stopPropagation();
                setElementLayout(element.id, 'right');
              }}
            >
              <i className="fas fa-arrow-right"></i>
            </ControlButton>
            
            <ControlButton 
              title="Move element up" 
              onClick={(e) => {
                e.stopPropagation();
                moveElementUp(element.id);
              }}
            >
              <i className="fas fa-arrow-up"></i>
            </ControlButton>
            
            <ControlButton 
              title="Move element down" 
              onClick={(e) => {
                e.stopPropagation();
                moveElementDown(element.id);
              }}
            >
              <i className="fas fa-arrow-down"></i>
            </ControlButton>
            
            <ControlButton 
              title="Duplicate" 
              onClick={(e) => {
                e.stopPropagation();
                duplicateElement && duplicateElement(element.id);
              }}
            >
              <i className="fas fa-copy"></i>
            </ControlButton>
            
            <ControlButton 
              title="Delete" 
              className="delete"
              onClick={(e) => {
                e.stopPropagation();
                removeElement(element.id);
              }}
            >
              <i className="fas fa-trash-alt"></i>
            </ControlButton>
          </HoverControls>
          
          {/* Element type indicator - only shown when selected or hovered */}
          <ElementType className={showControls || isSelected ? 'visible' : ''}>
            {element.type}
          </ElementType>
          
          <ElementContent>
            {renderElementContent()}
          </ElementContent>
        </ElementContainer>
      )}
    </Draggable>
  );
};

const ElementContainer = styled.div`
  position: relative;
  margin: 10px 0;
  border: 2px solid ${props => props.isDragging ? '#5876ff' : props.isSelected ? '#5876ff' : 'transparent'};
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: ${props => props.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : props.isSelected ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'};
  width: ${props => props.layout === 'left' || props.layout === 'right' ? '48%' : '100%'};
  float: ${props => props.layout === 'left' ? 'left' : props.layout === 'right' ? 'right' : 'none'};
  clear: ${props => props.layout === 'full' ? 'both' : 'none'};
  
  &:hover {
    border-color: #5876ff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
`;

// Removed ElementControls as we're using a different approach


const ElementType = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 11px;
  font-weight: 500;
  color: #888;
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: capitalize;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
  
  &.visible {
    opacity: 1;
  }
`;

const HoverControls = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  opacity: 0;
  transform: translateY(-4px);
  transition: all 0.2s ease;
  z-index: 10;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 6px 8px;
  font-size: 12px;
  transition: all 0.2s ease;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f0f2f5;
    color: #5876ff;
  }
  
  &.delete:hover {
    background-color: #fff0f0;
    color: #ff5252;
  }
`;

const ElementContent = styled.div`
  position: relative;
  padding: 10px 0;
  
  &:hover {
    cursor: pointer;
  }
`;

const HeaderElement = styled.div`
  font-size: ${props => props.style?.fontSize || '24px'};
  font-weight: ${props => props.style?.fontWeight || 'bold'};
  color: ${props => props.style?.color || '#333333'};
  text-align: ${props => props.style?.textAlign || 'center'};
  padding: ${props => props.style?.padding || '20px'};
  background-color: ${props => props.style?.backgroundColor || 'transparent'};
`;

const TextElement = styled.div`
  font-size: ${props => props.style?.fontSize || '16px'};
  color: ${props => props.style?.color || '#333333'};
  text-align: ${props => props.style?.textAlign || 'left'};
  padding: ${props => props.style?.padding || '10px 20px'};
  background-color: ${props => props.style?.backgroundColor || 'transparent'};
`;

const ImageContainer = styled.div`
  text-align: ${props => props.style?.textAlign || 'center'};
  padding: ${props => props.style?.padding || '10px'};
  background-color: ${props => props.style?.backgroundColor || 'transparent'};
`;

const ButtonContainer = styled.div`
  text-align: ${props => props.style?.textAlign || 'center'};
  padding: ${props => props.style?.padding || '15px'};
  background-color: ${props => props.style?.backgroundColor || 'transparent'};
`;

const ButtonElement = styled.div`
  display: inline-block;
  padding: 10px 20px;
  font-size: ${props => props.style?.fontSize || '16px'};
  font-weight: ${props => props.style?.fontWeight || 'bold'};
  text-align: center;
  cursor: pointer;
`;

const SpacerElement = styled.div`
  width: 100%;
`;

const DividerContainer = styled.div`
  padding: ${props => props.style?.padding || '10px 0'};
  background-color: ${props => props.style?.backgroundColor || 'transparent'};
`;

const FooterElement = styled.div`
  font-size: ${props => props.style?.fontSize || '12px'};
  color: ${props => props.style?.color || '#666666'};
  text-align: ${props => props.style?.textAlign || 'center'};
  padding: ${props => props.style?.padding || '20px'};
  background-color: ${props => props.style?.backgroundColor || '#f5f5f5'};
`;

const UnsubscribeLink = styled.div`
  margin-top: 10px;
  
  a {
    text-decoration: underline;
  }
`;

export default EmailElement;
